import { supabase } from './client'
import { patientProfileService } from './patientProfile.service'
import { providerDashboardService } from './providerDashboard.service'
import { providerAuthService } from './providerAuth.service'

export type SetupGuideRole = 'patient' | 'provider'

export type SetupGuideItemKey =
  | 'profile-photo'
  | 'address'
  | 'emergency-contacts'
  | 'service-rates'
  | 'service-area'
  | 'availability'
  | 'training'

export interface SetupGuideItem {
  key: SetupGuideItemKey
  title: string
  description: string
  actionLabel: string
  isComplete: boolean
  isOptional?: boolean
}

export interface SetupGuideStatus {
  role: SetupGuideRole
  items: SetupGuideItem[]
  completedCount: number
  remainingCount: number
  totalCount: number
  isDismissed: boolean
  dismissedAt: string | null
}

const SETTINGS_TABLE = {
  patient: {
    table: 'patient_settings',
    actorColumn: 'patient_id',
    fallbackColumn: 'patient_auth_id',
  },
  provider: {
    table: 'provider_settings',
    actorColumn: 'provider_id',
    fallbackColumn: 'provider_auth_id',
  },
} as const

const unsupportedFallbackColumns = new Set<string>()

const getFallbackCacheKey = (table: string, column: string) => `${table}.${column}`

const isMissingColumnError = (error: unknown, column: string) => {
  if (!error || typeof error !== 'object') return false

  const code = 'code' in error ? String((error as { code?: unknown }).code ?? '') : ''
  const message = 'message' in error ? String((error as { message?: unknown }).message ?? '') : ''
  return code === '42703' || message.includes(column)
}

const summarizeGuide = (
  role: SetupGuideRole,
  items: SetupGuideItem[],
  dismissedAt: string | null,
): SetupGuideStatus => {
  const completedCount = items.filter((item) => item.isComplete).length
  const remainingCount = items.length - completedCount
  return {
    role,
    items,
    completedCount,
    remainingCount,
    totalCount: items.length,
    isDismissed: remainingCount === 0 && Boolean(dismissedAt),
    dismissedAt,
  }
}

async function getAuthUserId(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  return user?.id ?? null
}

async function getCurrentActorId(role: SetupGuideRole): Promise<string | null> {
  const authId = await getAuthUserId()
  if (!authId) return null

  const table = role === 'patient' ? 'patients' : 'providers'
  const { data } = await supabase.from(table).select('id').eq('auth_id', authId).maybeSingle()
  return (data as { id?: string } | null)?.id ?? null
}

async function getDismissedAt(role: SetupGuideRole, actorId: string): Promise<string | null> {
  const { table, actorColumn, fallbackColumn } = SETTINGS_TABLE[role]
  const fallbackCacheKey = fallbackColumn ? getFallbackCacheKey(table, fallbackColumn) : null

  let { data, error } = await supabase
    .from(table)
    .select('setup_guide_dismissed_at')
    .eq(actorColumn, actorId)
    .maybeSingle()

  if ((error || !data) && fallbackColumn && fallbackCacheKey && !unsupportedFallbackColumns.has(fallbackCacheKey)) {
    const authId = await getAuthUserId()
    if (authId) {
      const fallbackResult = await supabase
        .from(table)
        .select('setup_guide_dismissed_at')
        .eq(fallbackColumn, authId)
        .maybeSingle()

      if (isMissingColumnError(fallbackResult.error, fallbackColumn)) {
        unsupportedFallbackColumns.add(fallbackCacheKey)
      }

      data = fallbackResult.data
      error = fallbackResult.error
    }
  }

  if (error) return null
  return ((data as { setup_guide_dismissed_at?: string | null } | null)?.setup_guide_dismissed_at) ?? null
}

async function updateDismissedState(role: SetupGuideRole, dismissedAt: string | null): Promise<{ success: boolean; error?: string }> {
  try {
    const actorId = await getCurrentActorId(role)
    const authId = await getAuthUserId()
    if (!actorId && !authId) {
      return { success: false, error: 'User not found' }
    }

    const { table, actorColumn, fallbackColumn } = SETTINGS_TABLE[role]
    const fallbackCacheKey = fallbackColumn ? getFallbackCacheKey(table, fallbackColumn) : null
    const basePayload: Record<string, unknown> = {
      setup_guide_dismissed_at: dismissedAt,
    }

    if (actorId) {
      const existingPrimary = await supabase
        .from(table)
        .select('id')
        .eq(actorColumn, actorId)
        .maybeSingle()

      if (existingPrimary.data?.id) {
        const { error } = await supabase.from(table).update(basePayload).eq('id', existingPrimary.data.id)
        if (!error) return { success: true }
      } else {
        const payload = { ...basePayload, [actorColumn]: actorId }
        const { error } = await (supabase.from(table) as any).upsert(payload, { onConflict: actorColumn })
        if (!error) return { success: true }
      }
    }

    if (fallbackColumn && authId && fallbackCacheKey && !unsupportedFallbackColumns.has(fallbackCacheKey)) {
      const existingFallback = await supabase
        .from(table)
        .select('id')
        .eq(fallbackColumn, authId)
        .maybeSingle()

      if (isMissingColumnError(existingFallback.error, fallbackColumn)) {
        unsupportedFallbackColumns.add(fallbackCacheKey)
        return { success: false, error: 'Legacy settings column unavailable' }
      }

      const error = existingFallback.data?.id
        ? (await supabase.from(table).update(basePayload).eq('id', existingFallback.data.id)).error
        : (await (supabase.from(table) as any).insert({ ...basePayload, [fallbackColumn]: authId })).error

      if (isMissingColumnError(error, fallbackColumn)) {
        unsupportedFallbackColumns.add(fallbackCacheKey)
        return { success: false, error: 'Legacy settings column unavailable' }
      }

      if (error) throw error
      return { success: true }
    }

    return { success: false, error: 'Failed to update setup guide state' }
  } catch (error: any) {
    return { success: false, error: error?.message || 'Failed to update setup guide state' }
  }
}

export const setupGuideService = {
  async getPatientGuideStatus(): Promise<SetupGuideStatus> {
    const [profile, addresses, emergencyContacts] = await Promise.all([
      patientProfileService.getProfile(),
      patientProfileService.getAddresses(),
      patientProfileService.getEmergencyContacts(),
    ])

    const dismissedAt = profile?.id ? await getDismissedAt('patient', profile.id) : null

    const items: SetupGuideItem[] = [
      {
        key: 'profile-photo',
        title: 'Add profile photo',
        description: 'Help providers recognize your account quickly.',
        actionLabel: profile?.profile_image_url ? 'Review' : 'Add photo',
        isComplete: Boolean(profile?.profile_image_url),
      },
      {
        key: 'address',
        title: 'Add address',
        description: 'Keep your home address ready for bookings and visits.',
        actionLabel: addresses.length > 0 ? 'Manage' : 'Add address',
        isComplete: addresses.length > 0,
      },
      {
        key: 'emergency-contacts',
        title: 'Add emergency contacts',
        description: 'Store at least one contact for urgent care situations.',
        actionLabel: emergencyContacts.length > 0 ? 'Manage' : 'Add contact',
        isComplete: emergencyContacts.length > 0,
      },
    ]

    return summarizeGuide('patient', items, dismissedAt)
  },

  async getProviderGuideStatus(): Promise<SetupGuideStatus> {
    const provider = await providerAuthService.getProfile()
    if (!provider?.id) {
      return summarizeGuide('provider', [], null)
    }

    const [serviceAreas, availability, inductionStatus, serviceRates, dismissedAt] = await Promise.all([
      providerDashboardService.getServiceAreas(provider.id),
      providerDashboardService.getAvailability(provider.id),
      providerAuthService.getInductionStatus(),
      providerDashboardService.getServiceRates(provider.id),
      getDismissedAt('provider', provider.id),
    ])

    const hasActiveRates = serviceRates.some((rate) => Boolean(rate.is_active) && Number(rate.base_rate || 0) > 0)
    const hasActiveServiceArea = serviceAreas.some((area: any) => area?.is_active === true)
    const hasAvailability = availability.some(
      (day: any) => day?.is_available === true && Array.isArray(day?.provider_time_slots) && day.provider_time_slots.length > 0,
    )

    const items: SetupGuideItem[] = [
      {
        key: 'profile-photo',
        title: 'Add profile photo',
        description: 'A complete profile helps patients trust you faster.',
        actionLabel: provider.profile_image_url ? 'Review' : 'Add photo',
        isComplete: Boolean(provider.profile_image_url),
      },
      {
        key: 'service-rates',
        title: 'Set service rates',
        description: 'Make your pricing visible before patients book.',
        actionLabel: hasActiveRates ? 'Review' : 'Set rates',
        isComplete: hasActiveRates,
      },
      {
        key: 'service-area',
        title: 'Turn on service area',
        description: 'Choose where you are available to travel.',
        actionLabel: hasActiveServiceArea ? 'Review' : 'Set area',
        isComplete: hasActiveServiceArea,
      },
      {
        key: 'availability',
        title: 'Set availability',
        description: 'Open time slots so patients can request you.',
        actionLabel: hasAvailability ? 'Review' : 'Set hours',
        isComplete: hasAvailability,
      },
      {
        key: 'training',
        title: 'Go through provider training',
        description: 'Review platform rules, safety guidance, and workflows.',
        actionLabel: inductionStatus.isCompleted ? 'Review' : 'Start training',
        isComplete: inductionStatus.isCompleted === true,
        isOptional: true,
      },
    ]

    return summarizeGuide('provider', items, dismissedAt)
  },

  async dismissGuide(role: SetupGuideRole) {
    return updateDismissedState(role, new Date().toISOString())
  },

  async reopenGuide(role: SetupGuideRole) {
    return updateDismissedState(role, null)
  },
}